/**
 * GameBuilder - convenience factories for spawning common gameplay objects (player starts,
 * lighting, characters). Static helpers for level initialization and prototyping.
 */
import * as THREE from 'three';

import { Actor } from '../actors/Actor.js';
import { CharacterPawn } from '../actors/CharacterPawn.js';
import { PlayerController } from '../actors/PlayerController.js';
import { PlayerStart } from '../actors/PlayerStart.js';
import { SceneEnvironmentComponent } from '../components/index.js';
import { AmbientLightComponent } from '../components/lights/AmbientLightComponent.js';
import { DirectionalLightComponent } from '../components/lights/DirectionalLightComponent.js';
import { AnimationStateMachineComponent } from '../components/visual/AnimationStateMachineComponent.js';
import { FogComponent } from '../components/visual/FogComponent.js';
import { MeshComponent } from '../components/visual/MeshComponent.js';
import { CHARACTER_HEIGHT, CHARACTER_WIDTH, DEFAULT_CHARACTER_ANIMATION_CONFIG_URL, DEFAULT_SKYBOX_TEXTURE_URL, GROUND_SIZE, GROUND_UV_TILES_PER_UNIT } from '../const.js';
import { MaterialUtils } from '../materials.js';
import { PhysicsMotionType } from '../physics/PhysicsTypes.js';
import { DefaultCollisionProfile } from '../systems/collision/CollisionTypes.js';
import { MathHelpers } from '../utils/MathHelpers.js';

import type { World } from './World.js';
import type { FogComponentOptions, SceneEnvironmentOptions } from '../components/index.js';


export class GameBuilder {
  public static createPlayerStart(world: World, position?: THREE.Vector3, rotation?: THREE.Euler): PlayerStart {
    const playerStart = PlayerStart.create({
      position,
      rotation,
      name: 'Player Start',
    });
    world.addActors(playerStart);
    return playerStart;
  }

  public static createDefaultEnvironment(world: World, options?: SceneEnvironmentOptions): SceneEnvironmentComponent {
    if (!options || !('textureUrl' in options) || !options.textureUrl) {
      options = {
        textureUrl: DEFAULT_SKYBOX_TEXTURE_URL,
      };
    }

    const environmentComponent = SceneEnvironmentComponent.create(options);
    const environmentActor = Actor.create({
      rootComponent: environmentComponent,
      name: 'Environment',
    });
    world.addActors(environmentActor);
    return environmentComponent;
  }

  public static createDefaultLighting(world: World, includeAmbientLight: boolean = true): { directionalLight: DirectionalLightComponent, ambientLight: AmbientLightComponent | null } {
    const directionalLightComponent = DirectionalLightComponent.create({
      position: new THREE.Vector3(10, 50, -10),
      color: 0xffffff,
      intensity: 1,
      castShadow: true,
      shadowMapSize: 2048,
      shadowBias: 0,
      shadowNormalBias: 0,
      shadowNear: 0.1,
      shadowFar: 100,
      shadowRadius: 3,
      isSunLight: true,
    });
    directionalLightComponent.lookAt(0, 0, 0);

    const directionalLightActor = Actor.create({
      rootComponent: directionalLightComponent,
      name: 'Directional Light',
    });

    if (includeAmbientLight) {
      const ambientLightComponent = AmbientLightComponent.create({
        color: 0xffffff,
        intensity: 0.5,
      });

      const ambientLightActor = Actor.create({
        rootComponent: ambientLightComponent,
        name: 'Ambient Light',
      });
      world.addActors(directionalLightActor, ambientLightActor);
      return { directionalLight: directionalLightComponent, ambientLight: ambientLightComponent };
    } else {
      world.addActors(directionalLightActor);
      return { directionalLight: directionalLightComponent, ambientLight: null };
    }
  }

  public static createDefaultAnimatedCharacter(world: World, position?: THREE.Vector3, configUrl?: string): { pawn: CharacterPawn, controller: PlayerController } {
    position ??= MathHelpers.makeVector({ up: CHARACTER_HEIGHT / 2 });

    // Create a custom pawn class that uses the provided config URL
    const pawnClass = class extends CharacterPawn {
      protected override setupAnimationComponent(): AnimationStateMachineComponent | null {
        const animationComponent = AnimationStateMachineComponent.create({
          configUrl: configUrl ?? DEFAULT_CHARACTER_ANIMATION_CONFIG_URL
        });
        this.rootComponent.add(animationComponent);
        return animationComponent;
      }
    };

    const pawn = pawnClass.create();
    pawn.rootComponent.position.copy(position);

    const controller = PlayerController.create();
    controller.possess(pawn);
    world.addActors(pawn, controller);
    return { pawn, controller };
  }

  public static createDefaultGround(world: World, options: { size?: THREE.Vector3, position?: THREE.Vector3 } = {}): Actor {

    const groundComponent = MeshComponent.create({
      position: options.position ?? new THREE.Vector3(0, -CHARACTER_HEIGHT / 2, 0),
      geometry: new THREE.BoxGeometry(options.size?.x ?? GROUND_SIZE, options.size?.y ?? 0.1, options.size?.z ?? GROUND_SIZE),
      material: MaterialUtils.DefaultMaterial,
      uvScaleTilesPerUnit: GROUND_UV_TILES_PER_UNIT,
      physicsOptions: {
        enabled: true,
        motionType: PhysicsMotionType.Static,
        collisionProfile: DefaultCollisionProfile.BlockAll,
      }
    });
    const groundActor = Actor.create({
      rootComponent: groundComponent,
      name: 'Ground',
    });
    groundComponent.receiveShadow = true;
    world.addActors(groundActor);
    return groundActor;
  }

  public static createDefaultFog(world: World, options: FogComponentOptions = {}): Actor {
    const fogComponent = FogComponent.create(options);
    const fogActor = Actor.create({
      rootComponent: fogComponent,
      name: 'Fog',
    });
    world.addActors(fogActor);
    return fogActor;
  }

  /**
   * Creates a default scene with player start, skybox, lighting, and ground.
   * Usually used for editor scenes.
   */
  public static createDefaultEditorScene(world: World): void {
    GameBuilder.createDefaultLighting(world, false);
    GameBuilder.createDefaultGround(world);
    GameBuilder.createPlayerStart(world, new THREE.Vector3(0, CHARACTER_HEIGHT / 2, 0));
    GameBuilder.createDefaultEnvironment(world);
    console.log('Default scene created');
  }

  //====================================================================================================

  public static createDefaultPawnCapsuleGeometry(): THREE.CapsuleGeometry {
    const radius = CHARACTER_WIDTH / 2;
    return new THREE.CapsuleGeometry(radius, CHARACTER_HEIGHT - radius * 2);
  }
}
